/**
 * DealListRow — Cockpit Component
 * A single clickable row in the deal list. Priority-weighted height, left
 * accent bar, three content layers (agent+time, title+chip, tag line), and
 * a verdict chip. Fully keyboard-accessible.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 2 (height tiers + accent bar),
 *            WI-INBOX-ROW-CONTENT-001 (three content layers).
 * Token sources: ui-invariants.js (VERDICT_PILL, DS_COLORS),
 *                verdictPhrase.js (tone), task-display-config.js (COCKPIT_LABELS)
 */

import React, { useCallback } from 'react';
import { VERDICT_PILL, DS_COLORS } from '@/config/ui-invariants';
import { COCKPIT_LABELS } from '@/config/task-display-config';
import { deriveVerdictPhrase } from '@/lib/verdictPhrase';
import { formatRaise } from '@/lib/formatRaise';
import { VerdictChip } from './VerdictChip';

// Height per tone (priority-weighted)
const HEIGHT_BY_TONE = {
  strong:   96,
  moderate: 80,
  weak:     72,
  unclear:  56,
};

// Left accent bar color per tone (3px solid)
const ACCENT_BY_TONE = {
  strong:   DS_COLORS.text.success,         // green
  moderate: VERDICT_PILL.moderate.text,     // amber
  weak:     VERDICT_PILL.weak.text,         // amber
  unclear:  DS_COLORS.border.default,       // grey
};

// Title font size per tone (px)
const TITLE_SIZE_BY_TONE = {
  strong:   17,
  moderate: 16,
  weak:     15,
  unclear:  14,
};

// Title weight per tone — strong rows read heavier, pass rows quieter.
const TITLE_WEIGHT_BY_TONE = {
  strong:   800,
  moderate: 700,
  weak:     700,
  unclear:  600,
};

// Title colour per tone — unclear (pass) reads muted.
const TITLE_COLOR_BY_TONE = {
  strong:   DS_COLORS.text.primary,
  moderate: DS_COLORS.text.primary,
  weak:     DS_COLORS.text.primary,
  unclear:  DS_COLORS.text.muted,
};

// ── Pure helpers ──────────────────────────────────────────────────────────

/** Title-case a snake_case work_type if it is not in the central registry. */
function titleCaseFallback(workType) {
  if (!workType) return '';
  return workType
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getAgentLabel(workType) {
  const labels = COCKPIT_LABELS.agentLabels || {};
  return labels[workType] || titleCaseFallback(workType);
}

/** Format an ISO timestamp as 24h "HH:MM". Returns "" on falsy / invalid input. */
function formatRowTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Return the array of short tag strings to render on the bottom layer
 * for the given work item. Only truthy values are returned; an absent
 * field drops silently.
 */
function getRowTags(item) {
  const p = (item && item.payload) || {};
  const wt = item && item.work_type;

  switch (wt) {
    case 'tracxn_livedeals':
    case 'first_look': {
      const out = [];
      if (p.sector_hint) out.push(p.sector_hint);
      if (p.country)     out.push(p.country);
      if (p.round_type)  out.push(p.round_type);
      const raise = formatRaise({ usd: p.raise_amount_usd, inr: p.raise_amount_inr });
      if (raise) out.push(raise);
      return out;
    }
    case 'article_review': {
      const out = [];
      if (p.source_publication) out.push(p.source_publication);
      const sig = COCKPIT_LABELS.signalLabels?.[p.signal_strength];
      if (sig) out.push(sig);
      return out;
    }
    case 'linkedin_post': {
      const out = [];
      const postLabel = COCKPIT_LABELS.postTypeLabels?.[p.post_type];
      if (postLabel) out.push(postLabel);
      const sig = typeof p.signal_strength === 'string'
        ? COCKPIT_LABELS.signalLabels?.[p.signal_strength]
        : null;
      if (sig) out.push(sig);
      return out;
    }
    case 'lead_gen_scout': {
      const out = [];
      const fmt = COCKPIT_LABELS.scoutTagFormat || {};
      const count = Array.isArray(p.candidates) ? p.candidates.length : null;
      if (count) out.push(`${count}${fmt.candidatesSuffix || ' candidates'}`);
      if (p.funding_year) out.push(`${fmt.cohortPrefix || 'Cohort '}${p.funding_year}`);
      return out;
    }
    case 'deal_screen':
    case 'review_pitch_deck': {
      const out = [];
      if (p.round_type) out.push(p.round_type);
      return out;
    }
    default:
      return [];
  }
}

/**
 * @param {{
 *   item: object,
 *   selected: boolean,
 *   onSelect: (id: string) => void,
 * }} props
 */
export function DealListRow({ item, selected, onSelect }) {
  // DECISION: compute phrase+tone once per render to keep logic centralized.
  const { phrase, tone } = deriveVerdictPhrase(item);

  const height       = HEIGHT_BY_TONE[tone]        || 80;
  const accentColor  = ACCENT_BY_TONE[tone]        || DS_COLORS.border.default;
  const titleSize    = TITLE_SIZE_BY_TONE[tone]    || 16;
  const titleWeight  = TITLE_WEIGHT_BY_TONE[tone]  || 700;
  const titleColor   = TITLE_COLOR_BY_TONE[tone]   || DS_COLORS.text.primary;

  const title       = item.company || item.display_name || item.title || 'Untitled';
  const agentLabel  = getAgentLabel(item.work_type);
  const timeLabel   = formatRowTime(item.timestamp);
  const tags        = getRowTags(item);
  const tagLine     = tags.join(COCKPIT_LABELS.rowSeparator || ' · ');
  const showLayers  = tone !== 'unclear';

  const handleClick = useCallback(() => {
    onSelect(item.id);
  }, [item.id, onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item.id);
    }
  }, [item.id, onSelect]);

  // Selected state: warm background + 2px brand border.
  // Non-selected hover: slate surface via inline style (onMouseEnter/Leave).
  // DECISION: inline style for hover keeps token consistency — Tailwind arbitrary
  // values cannot take a raw hex from DS_COLORS without escaping.
  const [hovered, setHovered] = React.useState(false);

  const backgroundStyle = selected
    ? DS_COLORS.surface.warm
    : hovered
      ? DS_COLORS.surface.slate
      : DS_COLORS.surface.white;

  const borderStyle = selected
    ? `2px solid ${DS_COLORS.text.brand}`
    : '2px solid transparent';

  return (
    <div
      role="option"
      aria-selected={selected}
      tabIndex={0}
      data-testid="deal-list-row"
      data-row-id={item.id}
      data-tone={tone}
      data-selected={selected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !selected && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        cursor: 'pointer',
        background: backgroundStyle,
        border: borderStyle,
        borderLeft: `3px solid ${accentColor}`,
        padding: '0 16px',
        gap: 4,
        position: 'relative',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'background 120ms ease',
        userSelect: 'none',
      }}
    >
      {/* Layer 1 — agent label + timestamp (skipped for pass rows) */}
      {showLayers && (agentLabel || timeLabel) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'DM Sans, system-ui, sans-serif',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: DS_COLORS.text.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {agentLabel}
          </span>
          {timeLabel && (
            <span
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 11,
                color: DS_COLORS.text.muted,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {timeLabel}
            </span>
          )}
        </div>
      )}

      {/* Layer 2 — title + VerdictChip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontFamily: 'DM Sans, system-ui, sans-serif',
            fontSize: titleSize,
            fontWeight: titleWeight,
            color: titleColor,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: tone === 'unclear' ? 'line-through' : 'none',
            flex: 1,
            minWidth: 0,
          }}
        >
          {title}
        </div>
        <VerdictChip phrase={phrase} tone={tone} />
      </div>

      {/* Layer 3 — compact tag line (skipped for pass rows and when no tags) */}
      {showLayers && tags.length > 0 && (
        <div
          style={{
            fontFamily: 'DM Sans, system-ui, sans-serif',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: DS_COLORS.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {tagLine}
        </div>
      )}
    </div>
  );
}

export default DealListRow;
